import { setUser as sentrySetUser } from '@sentry/react';
import { isAxiosError } from 'axios';
import type { SubmitEvent } from 'react';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { getApiErrorMessage } from '@/api/errors';
import { appUrl } from '@/api/navigation';
import { useSsoLoginUrl } from '@/api/useSsoLoginUrl';
import { createLogger } from '@/utils';

import { brokerReturnUrl } from '../brokerReturnUrl';
import { nextPathFrom } from '../RequireLoggedOut';
import { useWhoAmI } from '../WhoAmI';

const log = createLogger('useLogin');

export const invalidLogin = 'Invalid username or password.';

export const ssoFailed =
  'Google sign-in failed. Make sure you use your company Google account.';

export const getLoginErrorMessage = (error: unknown) =>
  isAxiosError(error) ? getApiErrorMessage(error, invalidLogin) : invalidLogin;

export const useLogin = () => {
  const navigate = useNavigate();
  const { search } = useLocation();

  const { logIn } = useWhoAmI();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(() =>
    new URLSearchParams(search).get('error') === 'sso' ? ssoFailed : null,
  );
  const brokerUrl = brokerReturnUrl(new URLSearchParams(search).get('next'));
  const ssoLoginUrl = useSsoLoginUrl(
    brokerUrl ?? `${window.location.origin}${appUrl(nextPathFrom(search))}`,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isValid = username.length > 0 && password.length > 0;

  const handleSubmit = async (event: SubmitEvent) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const response = await logIn(username, password);
      log('handleSubmit %o', response);
      const next = nextPathFrom(search);
      sentrySetUser({ id: response.data.id });
      if (!brokerUrl) navigate(next);
    } catch (err) {
      setError(getLoginErrorMessage(err));
      setIsSubmitting(false);
    }
  };

  return {
    username,
    setUsername,
    password,
    setPassword,
    error,
    isValid,
    isSubmitting,
    handleSubmit,
    ssoLoginUrl,
  };
};

export type UseLoginReturn = ReturnType<typeof useLogin>;
